import { type MutableRefObject, useMemo } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { type GridSnapLiveRect, snapShapeCreationPosition } from '@/lib/guides/grid-snap'
import { expandMirrorSelectionMembers } from '@/lib/groups/mirror-group-expansion'
import { meetsTextDragThreshold } from '@/lib/text/text-tool-create'
import {
  isShapeMirrorEligible,
  mirrorShapeAcrossVerticalAxis,
} from '@/lib/transforms/mirror-shape-across-spine'
import {
  computeDragSpanRect,
  computePointerDragRect,
  exceedsPointerDragThreshold,
} from '../helpers/drag-rect'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { DragCreateState } from '../types/drag-create-state'
import type {
  MarqueeState,
  MarqueeZoomDragState,
  TextDragCreateState,
} from '../types/canvas-drag-state'
import type { useShapeDragCreate } from './use-shape-drag-create'

type ShapeDragCreate = ReturnType<typeof useShapeDragCreate>

/**
 * What the in-progress gestures preview on canvas: the shape being drag-created, the Mirror Move
 * ghosts, and the marquee / text-frame / zoom-marquee rects (canvas coordinates).
 */
export function useCreationPreviews({
  internalShapeManagerRef,
  scaleRef,
  dragCreate,
  selectedShape,
  computeShapeCreationParams,
  buildShapePathCommands,
  snapToGrid,
  documentGridSnapStepPt,
  gridSnapLiveRects,
  mirrorMoveHoverActive,
  mirrorSpineAxisX,
  marquee,
  textDragCreate,
  marqueeZoom,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  scaleRef: MutableRefObject<number>
  dragCreate: DragCreateState | null
  selectedShape: ShapeType | null
  computeShapeCreationParams: ShapeDragCreate['computeShapeCreationParams']
  buildShapePathCommands: ShapeDragCreate['buildShapePathCommands']
  snapToGrid: boolean
  documentGridSnapStepPt: number
  gridSnapLiveRects: GridSnapLiveRect[]
  mirrorMoveHoverActive: boolean
  mirrorSpineAxisX: CenterCanvasConfig['mirrorSpineAxisX']
  marquee: MarqueeState | null
  textDragCreate: TextDragCreateState | null
  marqueeZoom: MarqueeZoomDragState | null
}) {
  const dragPreviewShape = useMemo((): ShapeData | null => {
    if (!dragCreate || !selectedShape) return null
    if (!exceedsPointerDragThreshold(dragCreate, scaleRef.current)) return null

    const params = computeShapeCreationParams(dragCreate, selectedShape, 'preview')
    const styles = internalShapeManagerRef.current?.getLastUsedStyles?.()
    const fill = styles?.fill ?? '#e5e7eb'
    const stroke = styles?.stroke ?? '#000000'
    const strokeWidth = styles?.strokeWidth ?? 1
    let previewX = params.x
    let previewY = params.y

    if (snapToGrid && documentGridSnapStepPt > 0) {
      const snapped = snapShapeCreationPosition(
        previewX,
        previewY,
        params,
        documentGridSnapStepPt,
        gridSnapLiveRects
      )

      previewX = snapped.x
      previewY = snapped.y
    }

    const pathMirror = params.mirror
    const pathCommands = buildShapePathCommands(
      selectedShape,
      params.width,
      params.height,
      pathMirror
    )

    return {
      id: 'shape-preview',
      type: selectedShape,
      x: previewX,
      y: previewY,
      width: params.width,
      height: params.height,
      fill,
      stroke,
      strokeWidth,
      opacity: 1,
      pathCommands,
    }
  }, [
    dragCreate,
    selectedShape,
    computeShapeCreationParams,
    buildShapePathCommands,
    snapToGrid,
    documentGridSnapStepPt,
    gridSnapLiveRects,
    scaleRef,
    internalShapeManagerRef,
  ])

  /** Ghost preview of the mirrored position(s) while the user hovers the Mirror Move button —
   *  a selected group's descendants (any nesting depth) are folded in too, since the group
   *  shell alone has no visible content of its own. */
  const mirrorMovePreviewShapes = useMemo((): ShapeData[] => {
    if (!mirrorMoveHoverActive || mirrorSpineAxisX == null) return []

    const mgr = internalShapeManagerRef.current

    if (!mgr) return []

    const allShapes = mgr.getShapes()
    const shapesById = new Map(allShapes.map((s) => [s.id, s]))
    const selectedIds = mgr.getSelectedShapeIds()
    const selectedShapes = selectedIds
      .map((id) => shapesById.get(id))
      .filter((s): s is ShapeData => s != null)
    const eligibleShapes = selectedShapes.filter((s) => isShapeMirrorEligible(s, shapesById, []))
    const { shapes: shapesToPreview } = expandMirrorSelectionMembers(
      eligibleShapes,
      [],
      allShapes,
      []
    )

    return shapesToPreview.map((s) => ({
      ...s,
      ...mirrorShapeAcrossVerticalAxis(s, mirrorSpineAxisX),
      id: `mirror-preview-${s.id}`,
      opacity: (s.opacity ?? 1) * 0.5,
    }))
  }, [mirrorMoveHoverActive, mirrorSpineAxisX, internalShapeManagerRef])

  // Marquee preview rectangle (canvas coordinates)
  const marqueeRect = useMemo(() => {
    if (!marquee) return null

    return computePointerDragRect(marquee, scaleRef.current)
  }, [marquee, scaleRef])

  const textDragPreviewRect = useMemo(() => {
    if (!textDragCreate) return null
    const dx = textDragCreate.currentCanvasX - textDragCreate.startCanvasX
    const dy = textDragCreate.currentCanvasY - textDragCreate.startCanvasY

    if (!meetsTextDragThreshold(dx, dy, scaleRef.current)) return null
    return computeDragSpanRect(textDragCreate)
  }, [textDragCreate, scaleRef])

  const marqueeZoomRect = useMemo(() => {
    if (!marqueeZoom) return null

    return computePointerDragRect(marqueeZoom, scaleRef.current)
  }, [marqueeZoom, scaleRef])

  return {
    dragPreviewShape,
    mirrorMovePreviewShapes,
    marqueeRect,
    textDragPreviewRect,
    marqueeZoomRect,
  }
}
